// SPDX-License-Identifier: AGPL-3.0-or-later

import Authentication from '@app/features/auth/state/Authentication';
import type {Channel} from '@app/features/channel/models/Channel';
import * as MessageCommands from '@app/features/messaging/commands/MessageCommands';
import * as ReactionCommands from '@app/features/messaging/commands/ReactionCommands';
import type {Message} from '@app/features/messaging/models/MessagingMessage';
import Permission from '@app/features/permissions/state/Permission';
import styles from '@app/features/poll/components/PollCard.module.css';
import {type PollData, type PollOption, updatePollClosed} from '@app/features/poll/types/PollTypes';
import {Permissions} from '@fluxer/constants/src/ChannelConstants';
import {useLingui} from '@lingui/react/macro';
import {ChartBarIcon, CheckIcon, ClockIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {useCallback, useMemo} from 'react';

interface PollCardProps {
	message: Message;
	channel: Channel;
	poll: PollData;
}

export const PollCard = observer(({message, channel, poll}: PollCardProps) => {
	const {i18n} = useLingui();
	const currentUserId = Authentication.currentUserId;

	const isClosed = Boolean(
		poll.closed || (poll.expiresAt > 0 && Date.now() > poll.expiresAt),
	);

	const getOptionVotes = useCallback(
		(option: PollOption): number => {
			const reaction = message.reactions?.find((r) => r.emoji.name === option.emoji);
			return reaction?.count ?? 0;
		},
		[message.reactions],
	);

	const hasVotedForOption = useCallback(
		(option: PollOption): boolean => {
			const reaction = message.reactions?.find((r) => r.emoji.name === option.emoji);
			return Boolean(reaction?.me);
		},
		[message.reactions],
	);

	const totalVotes = useMemo(() => {
		return poll.options.reduce((sum, opt) => sum + getOptionVotes(opt), 0);
	}, [getOptionVotes, poll.options]);

	const canManagePoll = useMemo(() => {
		if (message.author.id === currentUserId) return true;
		if (channel.guildId) {
			return Permission.can(Permissions.MANAGE_MESSAGES, channel);
		}
		return false;
	}, [channel, currentUserId, message.author.id]);

	const handleVoteToggle = useCallback(
		(option: PollOption) => {
			if (isClosed) return;
			const alreadyVoted = hasVotedForOption(option);
			const emoji = {name: option.emoji};

			if (alreadyVoted) {
				ReactionCommands.removeReaction(i18n, channel.id, message.id, emoji);
			} else {
				if (!poll.allowMultiple) {
					// Remove votes from any other option first
					for (const other of poll.options) {
						if (other.id !== option.id && hasVotedForOption(other)) {
							ReactionCommands.removeReaction(i18n, channel.id, message.id, {
								name: other.emoji,
							});
						}
					}
				}
				ReactionCommands.addReaction(i18n, channel.id, message.id, emoji);
			}
		},
		[channel.id, hasVotedForOption, i18n, isClosed, message.id, poll.allowMultiple, poll.options],
	);

	const handleEndPoll = useCallback(() => {
		if (!canManagePoll || isClosed) return;
		const newContent = updatePollClosed(message.content, true);
		void MessageCommands.edit(channel.id, message.id, newContent);
	}, [canManagePoll, channel.id, isClosed, message.content, message.id]);

	const timeRemainingText = useMemo(() => {
		if (isClosed) return 'Final results • Closed';
		if (!poll.expiresAt || poll.expiresAt <= 0) return 'Ongoing poll';
		const diffMs = poll.expiresAt - Date.now();
		if (diffMs <= 0) return 'Final results • Closed';
		const hours = Math.floor(diffMs / (1000 * 60 * 60));
		const days = Math.floor(hours / 24);
		if (days > 1) return `Ends in ${days} days`;
		if (hours >= 1) return `Ends in ${hours} hr${hours > 1 ? 's' : ''}`;
		const mins = Math.max(1, Math.floor(diffMs / (1000 * 60)));
		return `Ends in ${mins} min${mins > 1 ? 's' : ''}`;
	}, [isClosed, poll.expiresAt]);

	return (
		<div className={styles.pollCard} data-flx="poll.poll-card">
			<div className={styles.pollHeader}>
				<div className={styles.questionRow}>
					<ChartBarIcon size={22} weight="bold" className={styles.pollIcon} />
					<span className={styles.questionText}>{poll.question}</span>
				</div>
				<span
					className={clsx(styles.badge, isClosed && styles.badgeClosed)}
				>
					{isClosed
						? 'Closed'
						: poll.allowMultiple
							? 'Select Multiple'
							: 'Select One'}
				</span>
			</div>

			<div className={styles.optionsList}>
				{poll.options.map((option) => {
					const votes = getOptionVotes(option);
					const percentage = totalVotes > 0 ? Math.round((votes / totalVotes) * 100) : 0;
					const voted = hasVotedForOption(option);

					return (
						<button
							key={option.id}
							type="button"
							className={clsx(
								styles.optionButton,
								voted && styles.optionButtonVoted,
							)}
							onClick={() => handleVoteToggle(option)}
							disabled={isClosed}
							title={isClosed ? 'Poll is closed' : `Vote for ${option.text}`}
						>
							<div
								className={clsx(
									styles.progressBar,
									voted && styles.progressBarVoted,
								)}
								style={{width: `${percentage}%`}}
							/>
							<div className={styles.optionContent}>
								<div className={styles.optionLeft}>
									<span className={styles.optionEmoji}>{option.emoji}</span>
									<span className={styles.optionText}>{option.text}</span>
								</div>
								<div className={styles.optionRight}>
									<span
										className={clsx(
											styles.voteCount,
											voted && styles.voteCountVoted,
										)}
									>
										{votes} {votes === 1 ? 'vote' : 'votes'} ({percentage}%)
									</span>
									<div
										className={clsx(
											styles.voteIndicator,
											voted && styles.voteIndicatorActive,
										)}
									>
										{voted && <CheckIcon size={12} weight="bold" />}
									</div>
								</div>
							</div>
						</button>
					);
				})}
			</div>

			<div className={styles.pollFooter}>
				<div className={styles.footerLeft}>
					<div className={styles.footerStat}>
						<span>
							{totalVotes} total {totalVotes === 1 ? 'vote' : 'votes'}
						</span>
					</div>
					<div className={styles.footerStat}>
						<ClockIcon size={14} />
						<span>{timeRemainingText}</span>
					</div>
				</div>

				{canManagePoll && !isClosed && (
					<button
						type="button"
						className={styles.endPollButton}
						onClick={handleEndPoll}
						title="End poll and finalize results"
					>
						End Poll
					</button>
				)}
			</div>
		</div>
	);
});
